import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './RejectionReasonModal.css';

interface RejectionReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  title?: string;
  submitButtonText?: string;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  rejectionReason?: string; // For viewing existing rejection reason
  isViewOnly?: boolean; // For profile page to just view the reason
}

const RejectionReasonModal: React.FC<RejectionReasonModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Provide Rejection Reason",
  submitButtonText = "Reject Listing",
  reasonLabel,
  reasonPlaceholder,
  rejectionReason = "",
  isViewOnly = false
}) => {
  const effectiveReasonLabel = reasonLabel ?? (isViewOnly ? 'Rejection Reason:' : 'Rejection reason (optional):');
  const effectivePlaceholder = reasonPlaceholder ?? (isViewOnly ? 'No reason provided' : 'Enter the reason for rejecting this listing (optional)...');
  const { isDarkMode } = useTheme();
  const [reason, setReason] = useState(rejectionReason);

  // Update local state when rejectionReason prop changes
  useEffect(() => {
    setReason(rejectionReason);
  }, [rejectionReason]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isViewOnly) {
      onSubmit(reason.trim());
      setReason('');
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <div className={`rejection-modal-overlay ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="rejection-modal">
        <div className="rejection-modal-header">
          <h2 className="rejection-modal-title">{title}</h2>
          <button className="rejection-modal-close" onClick={handleClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="rejection-modal-form">
          <div className="rejection-modal-content">
            <label htmlFor="rejection-reason" className="rejection-modal-label">
              {effectiveReasonLabel}
            </label>
            <textarea
              id="rejection-reason"
              className="rejection-modal-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={effectivePlaceholder}
              rows={4}
              maxLength={500}
              disabled={isViewOnly}
            />
            {!isViewOnly && (
              <div className="rejection-modal-char-count">
                {reason.length}/500 characters
              </div>
            )}
          </div>

          <div className="rejection-modal-actions">
            <button 
              type="button" 
              className="rejection-modal-btn rejection-modal-btn-secondary" 
              onClick={handleClose}
            >
              {isViewOnly ? 'Close' : 'Cancel'}
            </button>
            {!isViewOnly && (
              <button 
                type="submit" 
                className="rejection-modal-btn rejection-modal-btn-primary"
              >
                {submitButtonText}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RejectionReasonModal;
